import { CSSProperties } from 'react'
import styled from 'styled-components'
import { cover, timingFunctions } from 'polished'

type LazyImageProps = {
  'data-src'?: string
  'data-srcset'?: string
  cover?: boolean
  transformRule?: string
  zIndex?: Number
  rounded?: boolean
  height?: number
  width?: number
  preload?: boolean
  eager?: boolean
  style?: CSSProperties
}

/**
 * Helper for setting `data-sizes=auto` on the <img>, if a prop `data-srcset` is present.
 *
 * The `data-sizes=auto` property allows the lazysizes library to automatically handle selecting the right src from a srcset:
 * https://github.com/aFarkas/lazysizes#automatically-setting-the-sizes-attribute
 */
const automaticallyAddSizesAttributeWithSrcSet = (props: LazyImageProps) => {
  return {
    ...props,
    ...(props?.['data-srcset'] ? { 'data-sizes': 'auto' } : {}),
  }
}

const LazyImage = styled.img
  .attrs<LazyImageProps>(({ className = '', preload, eager, ...props }) => {
    // Route preload through the eager branch — lazysizes' `lazypreload` class
    // mutated DOM attrs before hydrateRoot ran, breaking hydration on heroes.
    if (eager || preload) {
      return {
        src: props['data-src'],
        srcSet: props['data-srcset'],
        // React 17 doesn't support fetchPriority as a JSX prop (added in 18.3),
        // so we use the lowercase HTML attribute name directly
        fetchpriority: 'high',
        loading: 'eager' as const,
      }
    }
    return {
      className: `${className} lazyload`,
      // lazysizes mutates the className from `lazyload` to `lazyloaded` on
      // each image as it finishes loading — and that mutation routinely
      // happens before React hydrates, so React sees a className diff and
      // (under React 18) errors out of hydration with #418. Tell React the
      // <img>'s attributes can drift from the SSR snapshot.
      suppressHydrationWarning: true,
      ...automaticallyAddSizesAttributeWithSrcSet(props),
    }
  })
  .withConfig({
    shouldForwardProp: (prop) => {
      return (
        [
          'cover',
          'transformRule',
          'zIndex',
          'rounded',
          'preload',
          'eager',
        ].includes(prop) !== true
      )
    },
  })<LazyImageProps>`
  width: ${({ width }) => (width ? `${width}px` : '100%')};
  height: ${({ height }) => (height ? `${height}px` : '100%')};
  object-fit: cover;

  ${({ rounded }) =>
    rounded &&
    `
    border-radius: 6px;
  `}

  ${({ transformRule }) =>
    transformRule &&
    `
    transform: ${transformRule};
  `}

  ${({ zIndex }) =>
    zIndex &&
    `
    z-index: ${zIndex};
  `}

  opacity: ${({ eager, preload }) => (eager || preload ? 1 : 0)};
  transition: opacity 180ms ease-out;
  transition-timing-function: ${timingFunctions('easeOutQuad')};

  &.lazyloaded {
    opacity: 1;
  }

  &.lazyload {
    opacity: 0;
  }

  ${(props) => props.cover && cover()}
`

export default LazyImage
